import { Price, PriceInterval } from '@griply/types';
import Dialog from '@mui/material/Dialog';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { useUpdateUserMetaData } from 'features/user';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { DialogVariantOptions } from 'shared/assets/styles';
import { useToday } from 'shared/contexts/today';
import { PremiumPurchased } from 'shared/dialogs/premium-purchased';
import {
  LoadingPurchasePremium,
  PurchasePremium,
  PurchasePremiumError,
} from 'shared/dialogs/purchase-premium';
import { useNetworkStatus } from 'shared/hooks/use-network-status';
import { useRedirectToStripeUrlOrStartTrial } from 'shared/hooks/use-redirect-to-stripe-url-or-start-trial';
import { useToggle } from 'shared/hooks/use-toggle';
import { useUser } from 'shared/hooks/use-user';
import { QueryParamOptions, useQueryParams } from 'shared/lib/react-router-dom';
import { getShouldShowRenewalNudge } from 'shared/utils/get-should-show-renewal-nudge';
import { getUserHasLifetime } from 'shared/utils/get-user-has-subscription';

import { getInitialPriceSelection } from './get-initial-price-selection';
import {
  PremiumDialogContext,
  PremiumDialogContextProps,
} from './premium-dialog-context';

export type PremiumDialogProviderProps = {
  children: React.ReactNode;
};

export const PremiumDialogProvider: React.FC<PremiumDialogProviderProps> = ({
  children,
}) => {
  const today = useToday();
  const user = useUser();
  const updateUserMetaData = useUpdateUserMetaData();

  const [
    {
      [QueryParamOptions.Premium]: premiumQueryParam,
      [QueryParamOptions.C]: discountCode,
      [QueryParamOptions.Purchase]: purchaseQueryParam,
      [QueryParamOptions.PriceType]: typeQueryParam,
    },
    setDiscountAndPremiumParams,
  ] = useQueryParams([
    QueryParamOptions.C,
    QueryParamOptions.Purchase,
    QueryParamOptions.Premium,
    QueryParamOptions.PriceType,
  ]);
  const [dialogOpen, _, toggleDialogForce] = useToggle(!!discountCode);
  const [initialPriceSelection, setInitialPriceSelection] = useState(
    getInitialPriceSelection(typeQueryParam),
  );
  const { redirectOrStartTrial: redirectToStripeUrl, isLoading } =
    useRedirectToStripeUrlOrStartTrial();
  const { isOnline } = useNetworkStatus();
  const { reset: resetQueryErrors } = useQueryErrorResetBoundary();

  const onSubmit = async (price?: Price) => {
    if (!price) {
      return;
    }

    const trialStarted = await redirectToStripeUrl({
      interval: price.interval,
      // The campaign code from the link outranks the one stored at signup.
      discountCode,
      coupon: price.coupon?.id,
    });

    // explicit check on boolean
    if (trialStarted === true) {
      setDiscountAndPremiumParams({ [QueryParamOptions.Premium]: 'true' });
      toggleDialogForce(false);
    }
  };

  const closeDialog = () => {
    toggleDialogForce(false);
    setDiscountAndPremiumParams({});

    // update renewal closed dates
    if (getShouldShowRenewalNudge(user, today)) {
      updateUserMetaData({
        renewDialogClosed: [
          ...(user?.user_metadata?.renewDialogClosed ?? []),
          today,
        ],
      });
    }
  };

  const clearPremiumQueryParam = () => setDiscountAndPremiumParams({});

  // FallbackComponent can't carry onClose; fallbackRender is the library's other shape
  const renderPurchasePremiumError = ({
    resetErrorBoundary,
  }: FallbackProps) => (
    <PurchasePremiumError onClose={closeDialog} onRetry={resetErrorBoundary} />
  );

  const value = useMemo<PremiumDialogContextProps>(
    () => ({
      openPremiumDialog: (priceInterval = PriceInterval.Year) => {
        setInitialPriceSelection(priceInterval);
        toggleDialogForce(true);
      },
    }),
    [toggleDialogForce],
  );

  useEffect(() => {
    // the nudge fetches prices on open; offline it would only open onto its error state
    if (!isOnline || !getShouldShowRenewalNudge(user, today)) {
      return;
    }
    toggleDialogForce(true);
  }, [isOnline, today, toggleDialogForce, user]);

  useEffect(() => {
    if (purchaseQueryParam === 'true') {
      // if a user already has lifetime, the user does not need to see a purchase dialog
      if (getUserHasLifetime(user)) {
        setDiscountAndPremiumParams({ [QueryParamOptions.C]: discountCode });
        return;
      }
      // open dialog
      toggleDialogForce(true);
    }
  }, [
    discountCode,
    purchaseQueryParam,
    setDiscountAndPremiumParams,
    toggleDialogForce,
    user,
  ]);

  return (
    <PremiumDialogContext.Provider value={value}>
      {children}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        variant={DialogVariantOptions.Premium}
      >
        <ErrorBoundary
          onReset={resetQueryErrors}
          fallbackRender={renderPurchasePremiumError}
        >
          <Suspense fallback={<LoadingPurchasePremium onClose={closeDialog} />}>
            <PurchasePremium
              onClose={closeDialog}
              onSubmit={onSubmit}
              isLoading={isLoading}
              initialPriceSelection={initialPriceSelection}
            />
          </Suspense>
        </ErrorBoundary>
      </Dialog>

      <PremiumPurchased
        open={premiumQueryParam === 'true'}
        onClose={clearPremiumQueryParam}
      />
    </PremiumDialogContext.Provider>
  );
};
